import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Brain,
  Check,
  X,
  AlertTriangle,
  RefreshCw,
  Key,
  Activity,
  Zap,
  DollarSign,
  Clock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { api } from '../../services/api';

interface AIProvider {
  provider_name: string;
  display_name: string;
  docs_url: string;
  is_enabled: boolean;
  priority: number;
  rate_limit_per_minute: number | null;
  rate_limit_per_day: number | null;
  current_usage_minute: number;
  current_usage_day: number;
  api_key_status: 'configured' | 'from_env' | 'not_configured';
  api_key_masked: string | null;
  api_key_set_at: string | null;
  is_healthy: boolean;
  last_health_check: string | null;
  base_url: string | null;
  default_model: string | null;
  cost_per_1k_input_tokens: number;
  cost_per_1k_output_tokens: number;
}

interface AIUsageStats {
  period: { start: string; end: string };
  by_provider: Record<string, {
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    total_tokens: number;
    estimated_cost: number;
    avg_latency_ms: number;
  }>;
  by_use_case: Record<string, {
    total_requests: number;
    total_tokens: number;
    estimated_cost: number;
  }>;
  daily: Array<{ date: string; total_requests: number; total_tokens: number; estimated_cost: number }>;
  totals: {
    requests: number;
    tokens: number;
    cost: number;
    avg_latency_ms: number;
  };
}

// API Key Modal Component
function APIKeyModal({
  provider,
  isOpen,
  onClose,
  onSave,
  isLoading,
}: {
  provider: AIProvider | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (key: string) => void;
  isLoading: boolean;
}) {
  const [apiKey, setApiKey] = useState('');

  if (!isOpen || !provider) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (apiKey.trim()) {
      onSave(apiKey.trim());
      setApiKey('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Configure {provider.display_name} API Key
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="api-key-input" className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              id="api-key-input"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your API key"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500">
              Get your API key from{' '}
              <a
                href={provider.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                {provider.display_name}
                <ExternalLink className="w-3 h-3 inline ml-1" />
              </a>
            </p>
          </div>

          {provider.api_key_status !== 'not_configured' && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                Current key: <span className="font-mono">{provider.api_key_masked}</span>
                {provider.api_key_status === 'from_env' && ' (from environment)'}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !apiKey.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Provider Card Component
function ProviderCard({
  provider,
  onConfigureKey,
  onToggle,
  onHealthCheck,
  onDeleteKey,
  isHealthChecking,
}: {
  provider: AIProvider;
  onConfigureKey: () => void;
  onToggle: () => void;
  onHealthCheck: () => void;
  onDeleteKey: () => void;
  isHealthChecking: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  // Show gray/unknown status if no key configured and never health checked
  const hasKey = provider.api_key_status !== 'not_configured';
  const hasBeenChecked = !!provider.last_health_check;

  const statusColor = !hasKey && !hasBeenChecked
    ? 'bg-gray-100 text-gray-600'
    : provider.is_healthy
    ? 'bg-green-100 text-green-700'
    : 'bg-red-100 text-red-700';

  const statusText = !hasKey && !hasBeenChecked
    ? 'Not Configured'
    : provider.is_healthy
    ? 'Healthy'
    : 'Unhealthy';

  const keyStatusColor =
    provider.api_key_status === 'configured'
      ? 'text-green-600'
      : provider.api_key_status === 'from_env'
      ? 'text-blue-600'
      : 'text-gray-400';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${provider.is_enabled ? 'bg-primary-100' : 'bg-gray-100'}`}>
              <Brain className={`w-5 h-5 ${provider.is_enabled ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{provider.display_name}</h3>
              <p className="text-xs text-gray-500">Priority: {provider.priority}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Health Status */}
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>
              {statusText}
            </span>

            {/* Enabled Toggle */}
            <button
              onClick={onToggle}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                provider.is_enabled ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                  provider.is_enabled ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* API Key Status */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className={`w-4 h-4 ${keyStatusColor}`} />
            <span className="text-sm text-gray-600">
              {provider.api_key_status === 'configured' ? (
                <span className="font-mono text-xs">{provider.api_key_masked}</span>
              ) : provider.api_key_status === 'from_env' ? (
                <span>
                  <span className="font-mono text-xs">{provider.api_key_masked}</span>
                  <span className="text-blue-600 ml-1">(env)</span>
                </span>
              ) : (
                <span className="text-gray-400">Not configured</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onConfigureKey}
              className="px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded"
            >
              {provider.api_key_status === 'not_configured' ? 'Add Key' : 'Update'}
            </button>
            {provider.api_key_status === 'configured' && (
              <button
                onClick={onDeleteKey}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
                title="Remove API key"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={onHealthCheck}
            disabled={isHealthChecking}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isHealthChecking ? 'animate-spin' : ''}`} />
            Check Health
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? 'Less' : 'More'}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Model:</span>
              <span className="ml-2 font-medium">{provider.default_model || 'Default'}</span>
            </div>
            <div>
              <span className="text-gray-500">Rate limit:</span>
              <span className="ml-2 font-medium">
                {provider.rate_limit_per_minute || 'Unlimited'}/min
              </span>
            </div>
            <div>
              <span className="text-gray-500">Input cost:</span>
              <span className="ml-2 font-medium">
                ${(provider.cost_per_1k_input_tokens * 1000).toFixed(4)}/1M
              </span>
            </div>
            <div>
              <span className="text-gray-500">Output cost:</span>
              <span className="ml-2 font-medium">
                ${(provider.cost_per_1k_output_tokens * 1000).toFixed(4)}/1M
              </span>
            </div>
            {provider.last_health_check && (
              <div className="col-span-2">
                <span className="text-gray-500">Last check:</span>
                <span className="ml-2 font-medium">
                  {new Date(provider.last_health_check).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function AISettings() {
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState<AIProvider | null>(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [healthCheckingProvider, setHealthCheckingProvider] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch providers
  const { data: providersData, isLoading: loadingProviders } = useQuery({
    queryKey: ['admin-ai-providers'],
    queryFn: () => api.getAIProviders(),
    refetchInterval: 30000,
  });

  // Fetch usage stats
  const { data: usageData } = useQuery({
    queryKey: ['admin-ai-usage'],
    queryFn: () => api.getAIUsageStats(),
  });

  // Auto health check state
  const [hasRunInitialHealthCheck, setHasRunInitialHealthCheck] = useState(false);

  // Update provider mutation
  const updateProviderMutation = useMutation({
    mutationFn: ({ name, updates }: { name: string; updates: Partial<AIProvider> }) =>
      api.updateAIProvider(name, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-providers'] });
      showSuccess('Provider updated');
    },
    onError: (error: Error) => showError(error.message),
  });

  // Set API key mutation
  const setApiKeyMutation = useMutation({
    mutationFn: ({ name, apiKey }: { name: string; apiKey: string }) =>
      api.setAIProviderApiKey(name, apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-providers'] });
      setShowKeyModal(false);
      setSelectedProvider(null);
      showSuccess('API key saved');
    },
    onError: (error: Error) => showError(error.message),
  });

  // Delete API key mutation
  const deleteApiKeyMutation = useMutation({
    mutationFn: (name: string) => api.deleteAIProviderApiKey(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-providers'] });
      showSuccess('API key removed');
    },
    onError: (error: Error) => showError(error.message),
  });

  // Health check mutation
  const healthCheckMutation = useMutation({
    mutationFn: (name: string) => api.triggerAIHealthCheck(name),
    onSuccess: (result, name) => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-providers'] });
      setHealthCheckingProvider(null);
      if (result.is_healthy) {
        showSuccess(`${name} is healthy (${result.latency_ms}ms)`);
      } else {
        showError(`${name}: ${result.error || 'Health check failed'}`);
      }
    },
    onError: (error: Error) => {
      setHealthCheckingProvider(null);
      showError(error.message);
    },
  });

  // Auto health check on page load for providers with keys
  useEffect(() => {
    if (providersData?.providers && !hasRunInitialHealthCheck && !loadingProviders) {
      setHasRunInitialHealthCheck(true);

      // Check health for providers that have keys configured
      const providersWithKeys = providersData.providers.filter(
        p => p.api_key_status !== 'not_configured' && p.is_enabled
      );

      // Trigger health checks sequentially to avoid rate limiting
      providersWithKeys.forEach((provider, index) => {
        setTimeout(() => {
          healthCheckMutation.mutate(provider.provider_name);
        }, index * 500); // 500ms between each check
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- healthCheckMutation is stable from useMutation
  }, [providersData, hasRunInitialHealthCheck, loadingProviders]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg: string) => {
    setErrorMessage(msg);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const handleToggleProvider = (provider: AIProvider) => {
    updateProviderMutation.mutate({
      name: provider.provider_name,
      updates: { is_enabled: !provider.is_enabled },
    });
  };

  const handleHealthCheck = (provider: AIProvider) => {
    setHealthCheckingProvider(provider.provider_name);
    healthCheckMutation.mutate(provider.provider_name);
  };

  const handleConfigureKey = (provider: AIProvider) => {
    setSelectedProvider(provider);
    setShowKeyModal(true);
  };

  const handleSaveKey = (apiKey: string) => {
    if (selectedProvider) {
      setApiKeyMutation.mutate({ name: selectedProvider.provider_name, apiKey });
    }
  };

  const handleDeleteKey = (provider: AIProvider) => {
    if (confirm(`Remove API key for ${provider.display_name}? The provider will fall back to environment variable.`)) {
      deleteApiKeyMutation.mutate(provider.provider_name);
    }
  };

  const providers = providersData?.providers || [];
  const usage = usageData as AIUsageStats | undefined;

  return (
    <div className="space-y-6">
      {/* Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {errorMessage}
        </div>
      )}

      {/* Usage Summary */}
      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Total Requests</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{usage.totals.requests.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Activity className="w-4 h-4" />
              <span className="text-sm">Total Tokens</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{usage.totals.tokens.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Estimated Cost</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">${usage.totals.cost.toFixed(4)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Avg Latency</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{usage.totals.avg_latency_ms}ms</p>
          </div>
        </div>
      )}

      {/* Providers Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Providers</h2>
        {loadingProviders ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((provider) => (
              <ProviderCard
                key={provider.provider_name}
                provider={provider}
                onConfigureKey={() => handleConfigureKey(provider)}
                onToggle={() => handleToggleProvider(provider)}
                onHealthCheck={() => handleHealthCheck(provider)}
                onDeleteKey={() => handleDeleteKey(provider)}
                isHealthChecking={healthCheckingProvider === provider.provider_name}
              />
            ))}
          </div>
        )}
      </div>

      {/* Usage by Provider */}
      {usage && Object.keys(usage.by_provider).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Provider (Last 30 Days)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Provider</th>
                  <th className="pb-2 font-medium text-right">Requests</th>
                  <th className="pb-2 font-medium text-right">Success</th>
                  <th className="pb-2 font-medium text-right">Tokens</th>
                  <th className="pb-2 font-medium text-right">Cost</th>
                  <th className="pb-2 font-medium text-right">Avg Latency</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(usage.by_provider).map(([name, stats]) => (
                  <tr key={name} className="border-b last:border-0">
                    <td className="py-2 font-medium capitalize">{name}</td>
                    <td className="py-2 text-right">{stats.total_requests.toLocaleString()}</td>
                    <td className="py-2 text-right">
                      <span className={stats.failed_requests > 0 ? 'text-amber-600' : 'text-green-600'}>
                        {((stats.successful_requests / stats.total_requests) * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 text-right">{stats.total_tokens.toLocaleString()}</td>
                    <td className="py-2 text-right">${stats.estimated_cost.toFixed(4)}</td>
                    <td className="py-2 text-right">{stats.avg_latency_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage by Use Case */}
      {usage && Object.keys(usage.by_use_case).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Feature</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(usage.by_use_case).map(([useCase, stats]) => (
              <div key={useCase} className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900 capitalize">
                  {useCase.replace(/_/g, ' ')}
                </p>
                <p className="text-lg font-bold text-gray-900">{stats.total_requests}</p>
                <p className="text-xs text-gray-500">${stats.estimated_cost.toFixed(4)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Brain className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">About AI Providers</p>
            <p className="text-sm text-blue-700 mt-1">
              API keys stored here are encrypted and take priority over environment variables.
              If no key is configured, the system will fall back to environment variables.
              Providers are tried in priority order (lowest first) when making AI requests.
            </p>
          </div>
        </div>
      </div>

      {/* API Key Modal */}
      <APIKeyModal
        provider={selectedProvider}
        isOpen={showKeyModal}
        onClose={() => {
          setShowKeyModal(false);
          setSelectedProvider(null);
        }}
        onSave={handleSaveKey}
        isLoading={setApiKeyMutation.isPending}
      />
    </div>
  );
}
